import type {DuplicateShop, Shop, ShopBranding, ValueOf} from '@myparcel-frontend/types';
import type {ComputedRef} from 'vue';
import {ApiException} from '@myparcel/sdk';
import {useSdk, Status} from '@myparcel-frontend/sdk';
import {pollUntil} from '@myparcel-frontend/utils';
import {kebabCase} from 'es-toolkit/string';
import {storeToRefs} from 'pinia';
import {computed, ref} from 'vue';
import {ShopCreatedButNotFetchedError} from './ShopCreatedButNotFetchedError';
import {MaxSubdomainLength} from '@/constants/consumer-portal';
import {useConfigStore} from '@/store/config';

/**
 * Helper hook to manage shops
 * Methods:
 * - updateShop: Update a shop
 * - getShop: Get a shop by id
 * - fetchShop: Fetch a shop by id from the API
 * - addShop: Add a new shop
 */
export default function useShop() {
  const configStore = useConfigStore();
  const {currentShop, shops} = storeToRefs(configStore);
  const status = ref<ValueOf<typeof Status>>(Status.IDLE);

  const getAccentColor = (accentColor: string | undefined) => {
    if (accentColor && accentColor.replace('#', '')) {
      return accentColor;
    }

    return '#0F5C47';
  };

  const updateShop = async (shop: Shop) => {
    const {sdk} = useSdk();

    const getValidSubdomain = () => {
      if (shop.branding?.subdomain) {
        return shop.branding.subdomain;
      }

      return kebabCase(shop.name).slice(0, MaxSubdomainLength);
    };

    const getPortalToggles = () => {
      const portalToggles = {
        enable_track_trace: shop.branding?.enable_track_trace ?? false,
        enable_returns: shop.branding?.enable_returns ?? false,
        use_consumer_portal: shop.branding?.use_consumer_portal ?? false,
      };

      if (portalToggles.enable_returns || portalToggles.enable_track_trace) {
        portalToggles.use_consumer_portal = true;
      }

      return portalToggles;
    };

    const newBranding: Partial<ShopBranding> = {
      ...shop.branding,
      ...getPortalToggles(),
      subdomain: getValidSubdomain(),
      accent_color: getAccentColor(shop.branding?.accent_color),
    };

    const newShopData: Shop = {
      ...shop,
      branding: newBranding,
    };

    // Not all shops have delivery addresses since this is a platform specific feature
    if (!newShopData?.delivery_address) {
      delete newShopData.delivery_address;
    }

    // Fulfilment settings can not be updated through the shop API
    if (Object(newShopData?.general_settings).hasOwnProperty('picqer')) {
      delete newShopData.general_settings.picqer;
    }

    if (Object(newShopData?.general_settings).hasOwnProperty('fulfilment_mode')) {
      delete newShopData.general_settings.fulfilment_mode;
    }

    if (Object(newShopData?.general_settings).hasOwnProperty('autosplit')) {
      delete newShopData.general_settings.autosplit;
    }

    status.value = Status.PENDING;

    try {
      await sdk.putShop({
        body: [newShopData],
      });

      status.value = Status.RESOLVED;
    } catch (error) {
      status.value = Status.REJECTED;
      throw error;
    }

    configStore.updateShop(shop);
  };

  /**
   * Add a new shop. This uses the postShopDuplicate endpoint.
   */
  const addShop = async (shop: DuplicateShop): Promise<void> => {
    const {sdk} = useSdk();
    status.value = Status.PENDING;

    if (!shop.name) {
      status.value = Status.REJECTED;
      throw new Error('Shop name is required');
    }

    try {
      const [{id}] = await sdk.postShopDuplicate({
        body: [
          {
            name: shop.name,
            account_id: configStore.account.id,
          },
        ],
      });

      status.value = Status.RESOLVED;

      // Fetch the new shop to get all its details to prevent issues with missing data in the shop object.
      // This is needed because the postShop endpoint only returns the id of the new shop.
      // The shop may not be immediately available, so we retry for up to 10 seconds.
      const newShop = await pollUntil(
        () => sdk.getShop({path: {id}}).then((response) => response.shops[0]),
        (shop) => !!shop,
        {
          interval: 1000,
          timeout: 10000,
          onError: (error) => {
            // The API may return a 401 (Permission Denied) until the shop is fully propagated.
            // We silently ignore these errors and let pollUntil retry.
            if (error instanceof ApiException && error.data.errors[0]?.status !== 401) {
              throw error;
            }
          },
        },
      );

      if (!newShop) {
        throw new ShopCreatedButNotFetchedError();
      }

      configStore.insertShop(newShop);
    } catch (error) {
      status.value = Status.REJECTED;
      throw error;
    }
  };

  const fetchShop = async (shopId: number): Promise<void> => {
    const {sdk} = useSdk();

    const response = await sdk.getShop({path: {id: shopId}});
    const shop = response.shops[0];

    configStore.updateShop(shop);
  };

  const getShop = (shopId: number): ComputedRef<Shop> => {
    return computed(() => configStore.account.shops.find(({id}: {id: number}) => id === shopId) as Shop);
  };

  const getShopsByIds = (shopIds: string[] | number[]) => {
    return configStore.account.shops.filter((shop) => {
      return shopIds.find((id) => shop.id === Number(id));
    });
  };

  return {
    addShop,
    fetchShop,
    getShop,
    getShopsByIds,
    status,
    updateShop,
    currentShop,
    shops,
  };
}
